import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, watch } from 'vue'
import {
    createUseCase,
    deleteUseCase,
    microtrendUseCases,
    updateUseCase
} from '@/service/use-cases'
import { Locale } from '@/graphql/app/types'
import { htmlRenderedMarkdown } from '@/util/html-rendered-markdown'
import i18n from '@/plugins/i18n'
import { UseCase, UseCaseContent, UseCasesTextContent } from '@/ui/microtrend/use-cases/use-cases.types'
import UseCases from '@/ui/microtrend/use-cases/use-cases.vue'
import { industries } from '@/service/industry'
import { useAiTrendExplanation } from '@/composables/use-ai-trend-explanation'
import { track } from '@/plugins/tracker'

interface Props {
  microtrendId: string
  locale: Locale
}


export default /*#__PURE__*/_defineComponent({
  __name: 'use-cases-container',
  props: {
    microtrendId: null,
    locale: null
  },
  setup(__props: any) {

const props = __props as {
  microtrendId: string
  locale: Locale
}



const industryItems = ref<Array<string>>([])
const initialUseCases = ref<Array<UseCase>>([])

const { explain } = useAiTrendExplanation()

const useCasesTextContent: UseCasesTextContent = {
    tabTitles: {
        ai: translation('tabTitles.ai'),
        aiTooltip: translation('tabTitles.aiTooltip'),
        create: translation('tabTitles.create'),
        edit: translation('tabTitles.edit'),
        savedUseCases: translation('tabTitles.savedUseCases')
    },
    aiTranslations: {
        editButton: translation('aiExplanation.editButton'),
        saveButton: translation('aiExplanation.saveButton'),
        textPrefix: translation('aiExplanation.controls.industrySelect.pre'),
        textPlaceholder: translation('aiExplanation.controls.industrySelect.placeholder'),
        selectHeader: translation('aiExplanation.controls.industrySelect.selectHeader'),
        textPostfix: translation('aiExplanation.controls.industrySelect.post')
    },
    formTextContent: {
        title: {
            label: translation('form.title.label'),
            validationErrors: {
                required: translation('form.title.validationErrors.required')
            }
        },
        text: {
            label: translation('form.text.label'),
            validationErrors: {
                required: translation('form.text.validationErrors.required')
            }
        },
        submitButton: translation('form.submitButton'),
        createButton: translation('form.createButton'),
        cancelButton: translation('form.cancelButton')
    },
    savedUseCasesTextContent: {
        deleteConfirmationDialog: {
            title: translation('savedUseCases.deleteConfirmationDialog.title'),
            content: translation('savedUseCases.deleteConfirmationDialog.content'),
            cancelButton: translation('savedUseCases.deleteConfirmationDialog.cancelButton'),
            confirmButton: translation('savedUseCases.deleteConfirmationDialog.confirmButton')
        },
        metadataFormat: (createdOn: string, author: string) => translation('savedUseCases.metadataFormat', { createdOn, author }),
        deleteButton: translation('savedUseCases.deleteButton'),
        editButton: translation('savedUseCases.editButton')
    }
}

onMounted(async () => {
    industryItems.value = (await industries(props.locale)).map(industry => industry.title)
    await updateInitialUseCases()
})

async function updateInitialUseCases (): Promise<void> {
    if (props.microtrendId && props.locale) {
        initialUseCases.value = await microtrendUseCases(props.microtrendId, props.locale)
    }
}

async function explanation (industry: string): Promise<string> {
    track('microtrend', 'Generate AI use-case')

    return htmlRenderedMarkdown(await explain(props.microtrendId, industry, props.locale))
}

function translation (key: string, values?: Record<string, string>): string {
    return i18n.t(`components.microtrend.overlay.useCases.${key}`, values).toString()
}

watch(() => props.microtrendId, () => updateInitialUseCases(), { immediate: true })
watch(() => props.locale, () => updateInitialUseCases(), { immediate: true })

const handleDeleteUseCase = async (useCase: UseCase): Promise<void> => {
    track('microtrend', 'Delete use-case')

    await deleteUseCase(useCase.id)
}

const handleCreateUseCase = async (content: UseCaseContent): Promise<UseCase> => {
    track('microtrend', 'Create use-case')

    return await createUseCase(props.locale, props.microtrendId, content.title, content.text)
}

const handleUpdateUseCase = async (id: string, content: UseCaseContent) => {
    track('microtrend', 'Update use-case')

    return await updateUseCase(id, content, props.locale)
}

return { __sfc: true,props, industryItems, initialUseCases, explain, useCasesTextContent, updateInitialUseCases, explanation, translation, handleDeleteUseCase, handleCreateUseCase, handleUpdateUseCase, UseCases }
}

})